import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import { db } from '@/services/database'
import { captureService } from '@/services/capture-service'
import { captureRateLimited, releaseCapture, retainCapture, syncCaptureRate } from '@/services/capture-consumers'
import { blockReload, saveBeforeReload } from '@/services/reload-safety'
import { defaultBuffPreferences, defaultBuffReminderSettings, manualTimerSettings, validManualTimerSeconds, reminderSettings, remainingBuffSeconds, visibleBuffSeconds, type BuffManualTimerSettings, type BuffReminderSettings, type BuffDefinition, type BuffObservation, type BuffPreferences, type BuffRunState, type BuffSnapshot, type BuffSource } from '@/buffs/types'
import { ManualBuffTimers } from '@/buffs/manual-timer'
import { normalizeBuffImage } from '@/buffs/images'
import { BuffMonitor, type BuffResult } from '@/buffs/monitor'
import { BuffReminderRules, BuffSpeechQueue, currentNotice, noticeText, type BuffNotice } from '@/buffs/reminders'
import { setBuffOverlaySource } from '@/buffs/overlay-source'
import { useAppStore } from './app'
import { BuffClock, BuffSampling } from '@/buffs/sampling'
import type { BuffNumberRead } from '@/buffs/timer'
import { BuffSpeechPlayer } from '@/buffs/speech-player'
import { BuffReminderDiagnostics } from '@/buffs/reminder-diagnostics'
import { speechLanguage } from '@/buffs/system-voices'
import { systemVoiceOptions } from '@/i18n/voice-labels'
import { isSpeechCompatibilityError } from '@/buffs/speech-types'
import { MANBO_VOICE_ID, usesManbo } from '@/buffs/manbo'
import { buffSpeechCopy } from '@/i18n/buff-speech'

export const useBuffStore = defineStore('buffs', () => {
  const app = useAppStore()
  const runtimeClock = new BuffClock()
  const sampling = new BuffSampling()
  let appliedInterval = 0
  const definitions = shallowRef<BuffDefinition[]>([])
  const preferences = ref<BuffPreferences>(defaultBuffPreferences())
  const observations = shallowRef<BuffObservation[]>([])
  let recognizedObservations: BuffObservation[] = []
  const manualTimers = new ManualBuffTimers()
  let timerSourceId = captureService.selectionId, timerPreset = app.presetId
  const state = ref<BuffRunState>('idle')
  const requested = ref(false)
  const error = ref('')
  const speechError = ref('')
  const auditionError = ref('')
  const manboNeedsUnlock = ref(false)
  const speechPlaying = ref(false)
  const auditionPreparing = ref(false), voiceRevision = ref(0)
  const saveFailures = shallowRef<Record<string, { message: string; retry: () => Promise<void> }>>({})
  const saveError = computed(() => Object.values(saveFailures.value)[0]?.message ?? '')
  const rateLimited = ref(false)
  const now = ref(runtimeClock.now())
  const notices = shallowRef<BuffNotice[]>([])
  const diagnostics = shallowRef<Record<string, unknown>[]>([])
  const urls = new Map<string, string>()
  const enabled = computed(() => definitions.value.filter(item => item.enabled))
  let initialized = false
  let startupMaintenance: Promise<void> | undefined
  let loading: Promise<void> | null = null
  let run = 0
  let recognitionPreset = app.presetId
  let releaseReload: (() => void) | null = null
  let clock: ReturnType<typeof setInterval> | null = null
  let samples: number[] = []
  const rules = new BuffReminderRules()
  const reminderDiagnostics = new BuffReminderDiagnostics()
  const player = new BuffSpeechPlayer((type, reason) => {
    appendDiagnostic({ category: 'speech-audio', type, reason, at: runtimeClock.now() })
    if (type === 'fallback' && reason === 'not-allowed') manboNeedsUnlock.value = true
    if (type === 'started' && reason === 'manbo') manboNeedsUnlock.value = false
    if (type === 'started') { speechError.value = ''; auditionError.value = '' }
  })
  const unsubscribeVoices = player.voices.subscribe(() => { voiceRevision.value++ })
  const manboState = shallowRef(player.manbo.state)
  const unsubscribeManbo = player.manbo.subscribe(() => { manboState.value = player.manbo.state })
  const manboSelected = computed(() => usesManbo(app.locale, preferences.value.speech))
  const voiceOptions = computed(() => {
    void voiceRevision.value
    const manbo = { id: MANBO_VOICE_ID, name: buffSpeechCopy[app.locale].manbo, local: false, label: buffSpeechCopy[app.locale].manbo }
    return [...(app.locale.startsWith('zh') ? [manbo] : []), ...systemVoiceOptions(player.voices.options(app.locale), app.locale)]
  })
  const voiceLanguage = computed(() => speechLanguage(app.locale))
  const speechSupported = computed(() => { void voiceRevision.value; return manboSelected.value || player.voices.supported })
  const speechAvailability = computed(() => { void voiceRevision.value; return manboSelected.value ? 'ready' as const : player.voices.availability(app.locale, preferences.value.speech.voices?.[speechLanguage(app.locale)]) })
  function checkSpeech(signal = new AbortController().signal): Promise<void> { return player.voices.wait(signal) }
  function retrySpeech(): void {
    unlockSpeech(); speechError.value = ''; auditionError.value = ''
    void checkSpeech(); speech.retryFailed()
  }
  watch(speechAvailability, availability => {
    if (availability !== 'ready') return
    if (isSpeechCompatibilityError(speechError.value)) speechError.value = ''
    if (isSpeechCompatibilityError(auditionError.value)) auditionError.value = ''
  }, { flush: 'sync' })
  const speech = new BuffSpeechQueue(() => ({ observations: observations.value, preferences: preferences.value, locale: app.locale, definitions: definitions.value }), message => { speechError.value = message }, event => appendDiagnostic({ ...event, category: 'speech' }), runtimeClock.now, playing => { speechPlaying.value = playing }, message => { auditionError.value = message }, player, preparing => { auditionPreparing.value = preparing })
  function unlockSpeech(): void { if (!speechPlaying.value) { manboNeedsUnlock.value = false; player.unlock() } }
  const nameEdits = shallowRef(new Map<string, { token: number; dirty: boolean }>())
  let nameEditToken = 0
  function beginBuffSettings(id: string): number {
    const token = ++nameEditToken
    nameEdits.value = new Map(nameEdits.value).set(id, { token, dirty: nameEdits.value.get(id)?.dirty ?? false })
    return token
  }
  function deferNamePreparation(id: string, token: number): void {
    if (nameEdits.value.get(id)?.token !== token) return
    nameEdits.value = new Map(nameEdits.value).set(id, { token, dirty: true })
  }
  async function endBuffSettings(id: string, token: number): Promise<void> {
    await mutationQueue
    if (nameEdits.value.get(id)?.token !== token) return
    const next = new Map(nameEdits.value); next.delete(id); nameEdits.value = next
  }
  function manboNameDeferred(id: string): boolean { return nameEdits.value.get(id)?.dirty ?? false }
  const preparationTexts = computed(() => manboSelected.value ? [...new Set(definitions.value.filter(item => {
    const rule = reminderSettings(item, preferences.value)
    return item.enabled && rule.speech && rule.nearing && !manboNameDeferred(item.id) && !saveFailures.value[`${item.id}/name`]
  }).map(item => noticeText(item.name, app.locale)))].sort() : [])
  watch(() => JSON.stringify(preparationTexts.value), () => { if (initialized && !startupMaintenance) void player.manbo.prepare(preparationTexts.value) }, { flush: 'sync' })
  const prepareAfterWorker = () => { if (initialized && !startupMaintenance && manboSelected.value) void player.manbo.prepare(preparationTexts.value) }
  navigator.serviceWorker?.addEventListener('controllerchange', prepareAfterWorker)
  function maintainManbo(task: Promise<void>): Promise<void> {
    return task.catch(caught => { appendDiagnostic({ category: 'manbo-cache', type: 'cleanup-failed', reason: String(caught), at: runtimeClock.now() }) })
  }
  async function retryManbo(): Promise<void> {
    unlockSpeech()
    await initialize()
    await startupMaintenance
    await player.manbo.retry(preparationTexts.value)
  }
  async function manboCacheStatus(id: string) {
    const item = definitions.value.find(item => item.id === id)
    return item ? player.manbo.cacheStatus(noticeText(item.name, app.locale)) : 'missing' as const
  }

  function imageUrl(definition: BuffDefinition): string {
    if (!urls.has(definition.id)) urls.set(definition.id, URL.createObjectURL(definition.image))
    return urls.get(definition.id)!
  }
  const snapshot = computed<BuffSnapshot>(() => {
    return {
      state: state.value, layout: { ...preferences.value.layout }, now: now.value, locale: app.locale,
      items: definitions.value.map(definition => {
        const observation = observations.value.find(item => item.id === definition.id)
          ?? { id: definition.id, presence: 'unknown' as const, seconds: null, upperSeconds: null, capturedAt: now.value, cycle: 0, reason: 'idle' }
        const rule = reminderSettings(definition, preferences.value)
        return { ...observation, enabled: definition.enabled, name: definition.name, imageUrl: imageUrl(definition), nearing: definition.enabled && rule.nearing && (remainingBuffSeconds(observation, now.value) ?? Infinity) <= rule.threshold, visual: rule.visual }
      }),
      alerts: notices.value.filter(item => item.visual && currentNotice(item, observations.value, now.value) && now.value - item.at <= 5000).map(item => noticeText(item.name, app.locale)),
    }
  })
  function appendDiagnostic(value: Record<string, unknown>): void { diagnostics.value = [...diagnostics.value.slice(-239), value] }
  function syncTimerContext(): void {
    if (timerSourceId === captureService.selectionId && timerPreset === app.presetId) return
    timerSourceId = captureService.selectionId; timerPreset = app.presetId
    manualTimers.reset(); recognizedObservations = []; notices.value = []
    rules.reset(); reminderDiagnostics.reset(); speech.stop(); sampling.reset()
  }
  function advance(at = runtimeClock.now()): void {
    if (!requested.value) return
    syncTimerContext()
    now.value = at
    observations.value = manualTimers.apply(definitions.value, recognizedObservations, at)
    evaluateReminders()
  }
  function invalidateRecognition(reason: string): void {
    recognizedObservations = recognizedObservations.map(item => ({ ...item, presence: 'unknown', seconds: null, upperSeconds: null, reason }))
    advance()
  }
  function monitorFailed(message: string): void {
    if (!requested.value) return
    if (manualTimers.active) { monitor.stop(); invalidateRecognition('worker-failed') }
    else stop()
    state.value = 'error'; error.value = message
  }
  const monitor = new BuffMonitor(onResult, reason => {
    if (!requested.value || state.value === 'preparing') return
    if (state.value !== 'interrupted') appendDiagnostic({ at: runtimeClock.now(), reason })
    state.value = 'interrupted'
    sampling.reset(); updateSampling()
    invalidateRecognition(reason)
  }, monitorFailed, runtimeClock.now)
  function updateSampling(): void {
    if (!requested.value) return
    sampling.configure(app.performanceConfig?.buffRecognitionIntervalMs ?? 500)
    const thresholds = new Map(enabled.value.map(item => [item.id, reminderSettings(item, preferences.value).threshold]))
    const effective = sampling.interval(runtimeClock.now(), thresholds)
    monitor.setInterval(sampling.baseIntervalMs, effective)
    if (effective === appliedInterval) return
    appliedInterval = effective
    retainCapture('buffs', 1000 / effective)
    void syncCaptureRate().then(() => { rateLimited.value = captureRateLimited() })
    appendDiagnostic({ category: 'sampling', at: runtimeClock.now(), baseIntervalMs: sampling.baseIntervalMs, effectiveIntervalMs: effective })
  }
  function onResult(result: BuffResult): void {
    if (!requested.value) return
    syncTimerContext()
    now.value = runtimeClock.now()
    samples = [...samples.filter(at => result.capturedAt - at < 10_000), result.capturedAt]
    rateLimited.value = captureRateLimited()
    recognizedObservations = result.observations
    sampling.observe(result.observations, result.reads as Record<string, BuffNumberRead>); updateSampling()
    state.value = result.observations.some(item => item.presence !== 'unknown') ? 'running' : 'waiting'
    advance(now.value)
    appendDiagnostic({ at: now.value, capturedAt: result.capturedAt, ageMs: now.value - result.capturedAt, targets: enabled.value.length,
      implementation: 'buff-evidence-v3', frameId: result.frameId, sourceId: result.sourceId, sentAt: result.sentAt, sourceObservedAt: result.sourceObservedAt, timestampSource: result.timestampSource, captureMs: result.captureMs, captureIntervalMs: result.captureIntervalMs, skippedBusy: result.skippedBusy,
      baseIntervalMs: sampling.baseIntervalMs, effectiveIntervalMs: appliedInterval,
      acceptedFps: samples.length > 1 ? (samples.length - 1) * 1000 / (samples.at(-1)! - samples[0]!) : null,
      wasmHeapBytes: result.wasmHeapBytes, quality: result.quality, processingMs: result.processingMs, reads: result.reads, region: preferences.value.region, evidence: result.evidence,
      displayed: observations.value.map(item => ({ id: item.id, timerSource: item.timerSource, presence: item.presence, seconds: visibleBuffSeconds(item, now.value), cycle: item.cycle, reason: item.reason, timeReason: item.timeReason, box: item.box,
        endpoint: item.endpoint, estimatedEndAt: item.estimatedEndAt, iconEvidenceAt: item.iconEvidenceAt, numberEvidenceAt: item.numberEvidenceAt, validUntil: item.validUntil, numberValidUntil: item.numberValidUntil,
        numberHidden: item.numberHidden, predicted: item.predicted, predictionValidUntil: item.predictionValidUntil })) })
  }
  function evaluateReminders(): void {
    const events = rules.evaluate(observations.value, definitions.value, preferences.value, now.value)
    for (const event of events) appendDiagnostic({ category: 'reminder', type: 'generated', ...event })
    for (const change of reminderDiagnostics.update(observations.value, definitions.value, preferences.value, events, now.value)) appendDiagnostic(change)
    notices.value = [...notices.value.filter(item => now.value - item.at < 5000 && !events.some(event => event.id === item.id)), ...events]
    speech.reconcile(); speech.enqueue(events)
  }
  async function initialize(): Promise<void> {
    if (initialized) return
    if (loading) return loading
    loading = (async () => {
      const [items, saved] = await Promise.all([db.buffs.orderBy('order').toArray(), db.settings.get('buffPreferences')])
      const value = (saved?.value && typeof saved.value === 'object' ? saved.value : {}) as Partial<BuffPreferences>
      const defaults = defaultBuffPreferences()
      const migrateSpeech = (value.speechDefaultsVersion ?? 0) < 1
      const voices = { ...defaults.speech.voices, ...value.speech?.voices }
      if (migrateSpeech) voices['zh-CN'] = MANBO_VOICE_ID
      const legacyRules = { ...defaults.reminders, ...value.reminders }
      if ((value.reminderDefaultsVersion ?? 0) < 2 && legacyRules.threshold === 5) legacyRules.threshold = 10
      const nextPreferences: BuffPreferences = { ...defaults, speechDefaultsVersion: migrateSpeech ? defaults.speechDefaultsVersion : value.speechDefaultsVersion, layout: { ...defaults.layout, ...((value.reminderDefaultsVersion ?? 0) >= 5 ? value.layout : {}) }, speech: { voices, volume: value.speech?.volume ?? defaults.speech.volume, rate: value.speech?.rate ?? defaults.speech.rate }, region: value.region ?? defaults.region }
      const migrate = (value.reminderDefaultsVersion ?? 0) < 3
      const nextItems = items.map(item => ({ ...item, enabled: true, manualTimer: manualTimerSettings(item), reminders: reminderSettings({ ...item, reminders: { ...(migrate ? legacyRules : defaultBuffReminderSettings()), ...item.reminders } }, nextPreferences) }))
      const migrateBuffs = (value.reminderDefaultsVersion ?? 0) < 6 || items.some(item => !item.enabled || item.reminders?.visual === false)
      if (migrateBuffs || migrateSpeech) await db.transaction('rw', [db.buffs, db.settings], async () => {
        if (migrateBuffs) await db.buffs.bulkPut(nextItems)
        await db.settings.put({ key: 'buffPreferences', value: nextPreferences, updatedAt: Date.now() })
      })
      definitions.value = nextItems
      preferences.value = nextPreferences
      initialized = true
      startupMaintenance = maintainManbo(player.manbo.cleanupExpired())
      void startupMaintenance.then(() => { startupMaintenance = undefined; prepareAfterWorker() })
      captureService.addEventListener('status', onCaptureStatus)
      setBuffOverlaySource(() => snapshot.value, () => {
        const at = runtimeClock.now()
        // The main clock and companion heartbeat share one presentation cadence.
        if (at - now.value >= 250) { if (requested.value) advance(at); else now.value = at }
        monitor.pulse()
      }, reorder)
      void syncWithCapture()
    })().catch(caught => { state.value = 'error'; error.value = String(caught); throw caught }).finally(() => { loading = null })
    return loading
  }
  watch(() => [preferences.value.layout, preferences.value.region], () => {
    if (!initialized) return
    void mutate(() => persistPreferences(preferences.value)).catch(caught => { error.value = String(caught) })
  }, { deep: true, flush: 'sync' })
  let mutationQueue: Promise<unknown> = Promise.resolve()
  function mutate<T>(task: () => Promise<T>): Promise<T> {
    const result = mutationQueue.then(task)
    mutationQueue = result.catch(() => undefined)
    return result
  }
  function persistPreferences(value: BuffPreferences): Promise<void> {
    const copy = JSON.parse(JSON.stringify(value)) as BuffPreferences
    return saveBeforeReload('buff-preferences', () => db.settings.put({ key: 'buffPreferences', value: copy, updatedAt: Date.now() }).then(() => undefined))
  }
  function clearSaveFailure(key: string): void {
    const next = { ...saveFailures.value }; delete next[key]; saveFailures.value = next
  }
  function saveSetting(keys: string[], task: () => Promise<void>): Promise<void> {
    return mutate(async () => {
      try { await task(); keys.forEach(clearSaveFailure) }
      catch (caught) {
        const failure = { message: String(caught), retry: () => saveSetting(keys, task) }
        saveFailures.value = { ...saveFailures.value, ...Object.fromEntries(keys.map(key => [key, failure])) }
        throw caught
      }
    })
  }
  async function retrySaves(): Promise<void> {
    await Promise.allSettled([...new Set(Object.values(saveFailures.value))].map(failure => failure.retry()))
  }
  function updateSpeechSettings(changes: Partial<BuffPreferences['speech']>): Promise<void> {
    return saveSetting(Object.keys(changes).map(key => `speech/${key}`), async () => {
      const next = { ...preferences.value, speech: { ...preferences.value.speech, ...changes,
        voices: { ...preferences.value.speech.voices, ...changes.voices },
        volume: Math.max(0, Math.min(1, changes.volume ?? preferences.value.speech.volume)),
        rate: Math.max(.5, Math.min(2, changes.rate ?? preferences.value.speech.rate)),
      } }
      await persistPreferences(next)
      preferences.value.speech = next.speech
    })
  }
  async function add(image: Blob, name: string, source?: BuffSource): Promise<'added' | 'duplicate'> {
    return mutate(async () => {
      await initialize()
      const normalized = await normalizeBuffImage(image)
      if (definitions.value.some(item => item.fingerprint === normalized.fingerprint || (source?.kind === 'skill' && item.sourceSkillId === source.id) || (source?.kind === 'item' && item.sourceItemId === source.id))) return 'duplicate'
      const definition: BuffDefinition = { id: crypto.randomUUID(), name: name.trim().slice(0, 80) || `BUFF ${definitions.value.length + 1}`, ...normalized, ...(source?.kind === 'skill' ? { sourceSkillId: source.id } : source?.kind === 'item' ? { sourceItemId: source.id } : {}), enabled: true, order: Math.max(-1, ...definitions.value.map(item => item.order)) + 1, reminders: defaultBuffReminderSettings(), manualTimer: manualTimerSettings({}), updatedAt: Date.now() }
      await db.buffs.add(definition)
      definitions.value = [...definitions.value, definition]
      return 'added'
    })
  }
  async function update(id: string, changes: Partial<Pick<BuffDefinition, 'name'>> & { reminders?: Partial<BuffReminderSettings>; manualTimer?: Partial<BuffManualTimerSettings> }): Promise<void> {
    const keys = [...Object.keys(changes).filter(key => key !== 'reminders' && key !== 'manualTimer'), ...Object.keys(changes.reminders ?? {}).map(key => `reminders/${key}`), ...Object.keys(changes.manualTimer ?? {}).map(key => `manualTimer/${key}`)].map(key => `${id}/${key}`)
    return saveSetting(keys, async () => {
      const definition = definitions.value.find(item => item.id === id)
      if (!definition) return
      const manualTimer = { ...manualTimerSettings(definition), ...changes.manualTimer }
      if (!validManualTimerSeconds(manualTimer.seconds) || typeof manualTimer.enabled !== 'boolean') throw new Error('Invalid manual timer settings')
      const next = { ...definition, ...changes, enabled: true, manualTimer, reminders: reminderSettings({ ...definition, reminders: { ...definition.reminders, ...changes.reminders } }), name: (changes.name ?? definition.name).trim().slice(0, 80) || definition.name, updatedAt: Date.now() }
      await saveBeforeReload(`buff-${id}`, () => db.buffs.put(next).then(() => undefined))
      definitions.value = definitions.value.map(item => item.id === id ? next : item)
      advance()
      speech.reconcile()
    })
  }
  async function remove(id: string): Promise<void> {
    return mutate(async () => {
      const definition = definitions.value.find(item => item.id === id)
      if (!definition) return
      await saveBeforeReload(`buff-${id}`, () => db.buffs.delete(id))
      definitions.value = definitions.value.filter(item => item.id !== id)
      advance()
      Object.keys(saveFailures.value).filter(key => key.startsWith(`${id}/`)).forEach(clearSaveFailure)
      const url = urls.get(id); if (url) URL.revokeObjectURL(url); urls.delete(id)
    })
  }
  async function reorder(id: string, to: number): Promise<void> {
    return mutate(async () => {
      const entries = [...definitions.value], from = entries.findIndex(item => item.id === id)
      if (from < 0) return
      entries.splice(Math.max(0, Math.min(entries.length - 1, to)), 0, entries.splice(from, 1)[0]!)
      const sorted = entries.map((item, order) => ({ ...item, order }))
      await db.transaction('rw', db.buffs, () => db.buffs.bulkPut(sorted)); definitions.value = sorted
    })
  }
  async function configure(): Promise<void> {
    if (!requested.value) return
    if (!enabled.value.length) { stop(); return }
    const generation = ++run
    syncTimerContext()
    const presetChanged = recognitionPreset !== app.presetId
    recognitionPreset = app.presetId
    const previous = presetChanged ? [] : recognizedObservations
    if (presetChanged) { rules.reset(); sampling.reset(); speech.stop() }
    state.value = 'preparing'; recognizedObservations = []; advance(); speech.reconcile()
    try {
      await monitor.start(enabled.value, preferences.value.region, previous, false, app.presetId === 'gcw' ? 'gcw' : app.presetId === 'mxdc' ? 'mxdc' : 'default')
      if (generation === run && requested.value) state.value = 'waiting'
    } catch (caught) {
      if (generation !== run || !requested.value) return
      monitorFailed(String(caught))
    }
  }
  // Capture is selected only by the control panel. This consumer never opens a picker.
  async function startWithActiveCapture(): Promise<void> {
    if (requested.value || !enabled.value.length || captureService.status !== 'active') return
    requested.value = true; state.value = 'preparing'; error.value = ''; rateLimited.value = false
    updateSampling()
    releaseReload = blockReload(); rules.reset(); reminderDiagnostics.reset()
    clock = setInterval(() => { advance(); updateSampling() }, 250)
    await configure()
  }
  async function syncWithCapture(): Promise<void> {
    await initialize()
    if (!enabled.value.length) { if (requested.value) stop(); return }
    if (captureService.status === 'active') {
      if (state.value !== 'error') await startWithActiveCapture()
    } else if (requested.value) {
      const interrupted = captureService.status === 'interrupted' || captureService.status === 'error'
      stop()
      if (interrupted) state.value = 'interrupted'
    }
  }
  function onCaptureStatus(): void {
    // A deliberate new connection also allows recovery from an earlier worker failure.
    if (captureService.status === 'active' && !requested.value) state.value = 'idle'
    void syncWithCapture().catch(() => undefined)
  }
  async function retry(): Promise<void> {
    error.value = ''; state.value = 'idle'
    if (requested.value) await configure()
    else await syncWithCapture()
  }
  function stop(): void {
    run++; monitor.stop(); speech.stop(); requested.value = false; state.value = 'idle'; observations.value = []; notices.value = []; samples = []
    recognizedObservations = []; manualTimers.reset()
    sampling.reset(); appliedInterval = 0
    releaseReload?.(); releaseReload = null
    if (clock !== null) clearInterval(clock)
    clock = null
    releaseCapture('buffs')
  }
  watch(() => enabled.value.map(item => `${item.id}:${item.fingerprint}`).sort().join('|') + JSON.stringify([preferences.value.region, app.presetId]), () => {
    if (!initialized) return
    if (requested.value) void configure()
    else void syncWithCapture().catch(() => undefined)
  })
  watch(() => app.performanceConfig?.buffRecognitionIntervalMs, updateSampling)
  watch(() => JSON.stringify([app.locale, preferences.value.speech]), () => speech.cancelPendingPreview())
  onScopeDispose(() => {
    unsubscribeManbo()
    navigator.serviceWorker?.removeEventListener('controllerchange', prepareAfterWorker)
    captureService.removeEventListener('status', onCaptureStatus)
    stop(); unsubscribeVoices(); player.dispose(); setBuffOverlaySource(null)
    for (const url of urls.values()) URL.revokeObjectURL(url)
    urls.clear()
  })
  return { definitions, enabled, preferences, observations, state, requested, error, speechError, auditionError, speechPlaying, auditionPreparing, voiceOptions, voiceLanguage, speechSupported, speechAvailability, checkSpeech, unlockSpeech, manboState, manboSelected, manboNeedsUnlock, retryManbo, manboCacheStatus, beginBuffSettings, deferNamePreparation, endBuffSettings, manboNameDeferred, saveFailures, saveError, retrySaves, settleSettingWrites: () => mutationQueue.then(() => undefined), updateSpeechSettings, rateLimited, snapshot, notices, diagnostics, initialize, imageUrl, add, update, remove, reorder, retry, startWithActiveCapture, syncWithCapture, stop, retrySpeech, audition: (name?: string) => { auditionError.value = ''; void speech.preview(name) }, clearDiagnostics: () => { diagnostics.value = [] } }
})
